import { groupBy } from "@mnk/frontend/lib/array";
import { formatMonthDay } from "@mnk/frontend/lib/date";
import { trpc } from "@mnk/frontend/lib/trpc";
import type { DrawingImage } from "@mnk/shared/models/drawingImage";
import type { FlagAction } from "@mnk/shared/models/flagAction";
import type { FlagColumn } from "@mnk/shared/models/flagColumn";
import type { FlagRow } from "@mnk/shared/models/flagRow";
import { useEffect, useMemo, useState } from "react";
import { COLOR_CONFIG } from "./useFlagRowsAndActions";

export interface IcJCTItem {
  rampName: string;
  action: string;
  modifiedAction?: string;
  actionDate?: string;
  modified: boolean;
  note?: string;
}

interface Position {
  x: number;
  y: number;
}

interface Bounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface DragState {
  isDragging: boolean;
  startPosition: Position;
  currentPosition: Position;
}

interface Props {
  ctx: CanvasRenderingContext2D;
  data: {
    icJCTItems: IcJCTItem[];
    position: Position;
  };
}

export const useIcJCT = (quantityId: string, currentDrawingImage: DrawingImage) => {
  const { sectionName } = currentDrawingImage;
  const [IcJCTItems, setIcJCTItems] = useState<IcJCTItem[]>([]);
  const [dragState, setDragState] = useState<DragState | null>(null);

  const { data: icJCTFlagRows, isFetching: isFetchingIcJCTFlagRows } = trpc.getIcJCTFlagRows.useQuery(
    {
      quantityId,
      icJCTName: sectionName ?? "",
      drawingImageId: currentDrawingImage.id,
    },
    { enabled: !!sectionName },
  );

  const { data: flagActions, isFetching: isFetchingFlagActions } = trpc.getFlagActions.useQuery(
    {
      flagRowIds: icJCTFlagRows?.map((flagRow: FlagRow) => flagRow.id) ?? [],
    },
    {
      enabled: !!icJCTFlagRows && icJCTFlagRows.length > 0,
    },
  );

  const { data: flagColumns, isFetching: isFetchingFlagColumns } = trpc.getFlagColumns.useQuery({
    quantityId,
  });

  const groupByFlagColumn = useMemo(
    () => groupBy(flagColumns ?? [], (flagColumn: FlagColumn) => flagColumn.id),
    [flagColumns],
  );

  // テーブル設定
  const config = useMemo(
    () => ({
      tableWidth: 688,
      leftColumnWidth: 188,
      rowHeight: 38,
      fontSize: 18,
      padding: 6,
    }),
    [],
  );

  const rampCount = new Set(icJCTFlagRows?.map((row: FlagRow) => row.rampName)).size;

  // テーブルの境界を計算
  const getIcJCTInfoBounds = (position: Position): Bounds => {
    return {
      x: position.x,
      y: position.y,
      width: config.tableWidth,
      height: config.rowHeight * 4 + rampCount * config.rowHeight,
    };
  };

  // ドラッグ開始時の処理
  const handleDragStart = (startPosition: Position) => {
    setDragState({
      isDragging: true,
      startPosition,
      currentPosition: startPosition,
    });
  };

  // ドラッグ中の処理
  const handleDragMove = (currentPosition: Position) => {
    if (!dragState) return;

    const dx = currentPosition.x - dragState.startPosition.x;
    const dy = currentPosition.y - dragState.startPosition.y;

    setDragState({
      ...dragState,
      currentPosition: {
        x: dragState.currentPosition.x + dx,
        y: dragState.currentPosition.y + dy,
      },
      startPosition: currentPosition,
    });
  };

  // ドラッグ終了時の処理
  const handleDragEnd = () => {
    setDragState(null);
  };

  useEffect(() => {
    if (!icJCTFlagRows || !flagActions || !flagColumns) {
      setIcJCTItems([]);
      return;
    }
    const items: IcJCTItem[] = [];
    let counter = 1;

    for (const flagRow of icJCTFlagRows) {
      const flagActionsInFlagRow = flagActions.filter((flagAction: FlagAction) => flagAction.flagRowId === flagRow.id);
      for (const flagAction of flagActionsInFlagRow) {
        const flagColumn = groupByFlagColumn[flagAction.flagColumnId][0];
        if (!flagColumn?.name) continue;

        const actionDates: string[] = [];
        if (flagRow.dateFirst) actionDates.push(`施工日①: ${formatMonthDay(flagRow.dateFirst)}`);
        if (flagRow.dateSecond) actionDates.push(`施工日②: ${formatMonthDay(flagRow.dateSecond)}`);
        if (flagRow.dateThird) actionDates.push(`施工日③: ${formatMonthDay(flagRow.dateThird)}`);
        if (flagRow.dateFourth) actionDates.push(`施工日④: ${formatMonthDay(flagRow.dateFourth)}`);

        const actions = [];
        if (flagAction.value != null) {
          actions.push(`${flagColumn.name}:`);
          actions.push(flagAction.value ?? "");
          actions.push(flagColumn.unit ?? "");
        }

        const modifiedActions = [];
        if (flagAction.modifiedValue != null) {
          if (flagAction.value == null) {
            modifiedActions.push(`${flagColumn.name}:`);
          }
          modifiedActions.push(flagAction.modifiedValue ?? "");
          modifiedActions.push(flagColumn.unit ?? "");
        }

        const item: IcJCTItem = {
          rampName: `${counter}. ${flagRow.rampName ?? ""}`,
          action: actions.join(" "),
          modifiedAction: modifiedActions.length > 0 ? modifiedActions.join(" ") : undefined,
          actionDate: actionDates.join(" "),
          modified: flagAction.modifiedValue != null,
          note: flagAction.note ?? undefined,
        };
        items.push(item);
        counter++;
      }
    }
    setIcJCTItems(items);
  }, [icJCTFlagRows, flagActions, flagColumns, groupByFlagColumn]);

  // rampNameでグループ化する関数を追加
  const groupByRampName = (icJCTItems: IcJCTItem[]): { [key: string]: IcJCTItem[] } => {
    return icJCTItems.reduce(
      (acc, item) => {
        // 番号部分を除去して純粋なrampNameを取得（例: "1. RampA" → "RampA"）
        const cleanRampName = item.rampName.replace(/^\d+\.\s*/, "");
        const key = cleanRampName;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(item);
        return acc;
      },
      {} as { [key: string]: IcJCTItem[] },
    );
  };

  // テキストを指定幅で折り返す関数
  const wrapText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] => {
    const lines: string[] = [];
    let currentLine = "";

    // 文字単位で処理
    const chars = text.split("");

    for (let i = 0; i < chars.length; i++) {
      const char = chars[i];
      const testLine = currentLine + char;
      const metrics = ctx.measureText(testLine);

      if (metrics.width > maxWidth) {
        // 現在の行に文字がある場合は改行
        if (currentLine) {
          lines.push(currentLine);
          currentLine = char;
        } else {
          // 1文字でも幅を超える場合はそのまま追加
          lines.push(char);
          currentLine = "";
        }
      } else {
        currentLine = testLine;
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines;
  };

  const drawIcJCT = async (props: Props) => {
    const { ctx, data } = props;
    const { icJCTItems, position } = data;

    if (icJCTItems.length === 0) return;

    return new Promise<void>((resolve) => {
      const { rowHeight, fontSize, padding } = config;
      const { x, y } = dragState?.currentPosition ?? position;

      // フォントを先に設定
      ctx.font = `${fontSize}px Arial`;

      // 固定幅の設定
      const fixedTableWidth = 1350;
      const contentWidth = fixedTableWidth - padding * 2;

      // rampNameでグループ化
      const groupedItems = groupByRampName(icJCTItems);

      ctx.save();
      ctx.translate(x, y);

      let currentY = padding;

      // グループごとに描画
      for (const [rampName, items] of Object.entries(groupedItems)) {
        // グループ間に余白を追加（最初のグループ以外）
        if (currentY > padding) {
          currentY += rowHeight / 2; // 行間を半分に縮小
        }

        // 1セット = 単価名 当初値 変更後値 （備考）をスペース区切りでまとめる
        const sets: string[] = [];
        let actionDate = "";

        for (const [index, item] of items.entries()) {
          const setParts: string[] = [];
          if (item.action) setParts.push(item.action);
          if (item.modifiedAction) setParts.push(item.modifiedAction);
          if (item.note) setParts.push(`（${item.note}）`);
          if (setParts.length > 0) sets.push(setParts.join(" "));
          if (index === 0 && item.actionDate) {
            actionDate = item.actionDate;
          }
        }

        // ランプ名: セット1, セット2, ..., 施工日
        const setsStr = sets.join(", ");
        const lineText = actionDate
          ? `${rampName}: ${setsStr}, ${actionDate}`
          : `${rampName}: ${setsStr}`;

        // テキストを折り返し
        const wrappedLines = wrapText(ctx, lineText, contentWidth);

        // 各行のコンテンツを描画
        for (const line of wrappedLines) {
          currentY += rowHeight;

          // 修正された項目があるかチェック
          const hasModified = items.some((item) => item.modified);

          if (hasModified) {
            // 修正がある場合の色分け処理
            let currentX = padding;
            const chars = line.split("");
            let charIndex = 0;

            // 各文字を順番に処理して色分けを適用
            while (charIndex < chars.length) {
              let found = false;

              // 現在位置からのテキストを取得
              const remainingText = chars.slice(charIndex).join("");

              // rampNameのチェック
              if (remainingText.startsWith(rampName)) {
                ctx.fillStyle = "black";
                ctx.fillText(rampName, currentX, currentY - padding);
                currentX += ctx.measureText(rampName).width;
                charIndex += rampName.length;
                found = true;
              }

              // アクションのチェック（修正前・修正後）
              if (!found) {
                for (const item of items) {
                  if (item.modifiedAction && remainingText.startsWith(item.modifiedAction)) {
                    // 修正アクションを赤色で表示
                    ctx.save();
                    ctx.fillStyle = COLOR_CONFIG.red;
                    ctx.fillText(item.modifiedAction, currentX, currentY - padding);
                    ctx.restore();
                    currentX += ctx.measureText(item.modifiedAction).width;
                    charIndex += item.modifiedAction.length;
                    found = true;
                    break;
                  }
                  if (item.action && item.modifiedAction && remainingText.startsWith(item.action)) {
                    // 修正されたアクションの元のアクションを黄色背景で表示
                    const actionTextWidth = ctx.measureText(item.action).width;
                    ctx.save();
                    ctx.fillStyle = COLOR_CONFIG.yellow;
                    ctx.fillRect(currentX - 2, currentY - rowHeight + 2, actionTextWidth + 4, rowHeight - 4);
                    ctx.fillStyle = COLOR_CONFIG.black;
                    ctx.fillText(item.action, currentX, currentY - padding);
                    ctx.restore();
                    currentX += actionTextWidth;
                    charIndex += item.action.length;
                    found = true;
                    break;
                  }
                  if (item.action && !item.modifiedAction && remainingText.startsWith(item.action)) {
                    // 修正されていないアクションは通常表示
                    ctx.fillStyle = "black";
                    ctx.fillText(item.action, currentX, currentY - padding);
                    currentX += ctx.measureText(item.action).width;
                    charIndex += item.action.length;
                    found = true;
                    break;
                  }
                }
              }

              // actionDateのチェック
              if (!found && actionDate && remainingText.startsWith(actionDate)) {
                ctx.fillStyle = "black";
                ctx.fillText(actionDate, currentX, currentY - padding);
                currentX += ctx.measureText(actionDate).width;
                charIndex += actionDate.length;
                found = true;
              }

              // マッチしない文字（カンマ、スペースなど）
              if (!found) {
                const char = chars[charIndex];
                ctx.fillStyle = "black";
                ctx.fillText(char, currentX, currentY - padding);
                currentX += ctx.measureText(char).width;
                charIndex++;
              }
            }
          } else {
            // 修正がない場合は通常表示
            ctx.fillStyle = "black";
            ctx.fillText(line, padding, currentY - padding);
          }
        }
      }

      // 外枠を描画
      ctx.strokeStyle = "black";
      ctx.lineWidth = 2;
      ctx.strokeRect(0, 0, fixedTableWidth, currentY + padding);

      ctx.restore();
      resolve();
    });
  };

  const isLoading = isFetchingIcJCTFlagRows || isFetchingFlagActions || isFetchingFlagColumns;

  return {
    IcJCTItems,
    rampCount,
    drawIcJCT,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    isDragging: dragState?.isDragging ?? false,
    dragState,
    tableConfig: config,
    getIcJCTInfoBounds,
    isLoading,
  };
};
